import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import settingsService from '../../services/settingsService';
import { Check, X, Sparkles, Sliders } from 'lucide-react';

const WRITING_STYLES = [
  { value: 'formal', label: 'Formal', description: 'Professional and structured communication' },
  { value: 'casual', label: 'Casual', description: 'Friendly and conversational tone' },
  { value: 'technical', label: 'Technical', description: 'Precise and detail-oriented' },
  { value: 'creative', label: 'Creative', description: 'Imaginative and expressive' },
  { value: 'balanced', label: 'Balanced', description: 'Mix of formal and casual' }
];

const AIBehaviorSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    writing_style: 'balanced',
    tone_level: 3,
    auto_focus_suggestions: true
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userSettings = await settingsService.getUserSettings(user.id);
      setSettings({
        writing_style: userSettings.writing_style || 'balanced',
        tone_level: userSettings.tone_level || 3,
        auto_focus_suggestions: userSettings.auto_focus_suggestions ?? true
      });
    } catch (error) {
      console.error('Error loading AI behavior settings:', error);
      setMessage({ type: 'error', text: 'Failed to load AI behavior settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await settingsService.updateUserSettings(user.id, settings);

      setMessage({ type: 'success', text: 'AI behavior settings updated!' });
      setHasChanges(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving AI behavior settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const getToneLevelDescription = (level) => {
    const descriptions = {
      1: 'Very concise',
      2: 'Brief',
      3: 'Balanced',
      4: 'Detailed',
      5: 'Very detailed'
    };
    return descriptions[level] || 'Balanced';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">AI Behavior</h2>
        <p className="text-gray-600 mt-1">
          Customize how Swirl's AI interacts with you
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Writing Style */}
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Writing Style</span>
          <p className="text-xs text-gray-500 mt-1 mb-2">
            Choose how Swirl should communicate with you
          </p>
        </label>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {WRITING_STYLES.map(style => {
            const isSelected = settings.writing_style === style.value;
            
            return (
              <button
                key={style.value}
                onClick={() => handleChange('writing_style', style.value)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">{style.label}</span>
                  {isSelected && (
                    <Check size={18} className="text-purple-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600">{style.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tone Level Slider */}
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Response Detail Level</span>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            Control how detailed AI responses should be
          </p>
        </label>
        
        <div className="px-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Concise</span>
            <span className="text-sm font-semibold text-purple-600">
              {getToneLevelDescription(settings.tone_level)}
            </span>
            <span className="text-sm text-gray-600">Detailed</span>
          </div>
          
          <input
            type="range"
            min="1"
            max="5"
            step="1"
            value={settings.tone_level}
            onChange={(e) => handleChange('tone_level', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          
          <div className="flex justify-between mt-1">
            {[1, 2, 3, 4, 5].map(level => (
              <span
                key={level}
                className={`text-xs ${
                  settings.tone_level === level ? 'text-purple-600 font-semibold' : 'text-gray-400'
                }`}
              >
                {level}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Auto Focus Suggestions Toggle */}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Auto Focus Suggestions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Let Swirl suggest relevant focuses based on your activity
              </p>
            </div>
          </div>
          
          <button
            onClick={() => handleChange('auto_focus_suggestions', !settings.auto_focus_suggestions)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.auto_focus_suggestions ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.auto_focus_suggestions ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Example Preview */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Sliders size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-purple-900 mb-2">
              Preview: {WRITING_STYLES.find(s => s.value === settings.writing_style)?.label} Style
            </h4>
            <div className="text-sm text-purple-800 bg-white bg-opacity-50 rounded p-3">
              {settings.writing_style === 'formal' && (
                <p>I would be pleased to assist you with your inquiry. Based on your recent activity, I recommend focusing on project management tasks.</p>
              )}
              {settings.writing_style === 'casual' && (
                <p>Hey! I'd be happy to help with that! Looking at what you've been working on, maybe we should focus on those project management tasks?</p>
              )}
              {settings.writing_style === 'technical' && (
                <p>Analysis of your activity log indicates optimal resource allocation toward project management tasks. Processing recommendation engine...</p>
              )}
              {settings.writing_style === 'creative' && (
                <p>Let's dive into this together! Your recent journey through your work suggests an exciting opportunity to explore project management from a fresh angle.</p>
              )}
              {settings.writing_style === 'balanced' && (
                <p>I can help you with that. Based on your recent activity, it looks like focusing on project management tasks would be beneficial.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AIBehaviorSection;





